import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import JSZip from 'jszip';
import { v4 as uuidv4 } from 'uuid';
import type { Point, Line, Polygon } from '../../../context/context';
import type { FileDetectedType } from '../types';

export function detectFileType(file: File): FileDetectedType {
  const name = file.name.toLowerCase();
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'excel';
  if (name.endsWith('.csv')) return 'csv';
  if (name.endsWith('.zip')) return 'gtfs';
  if (name.endsWith('.kml')) return 'kml';
  return 'unknown';
}

export interface ParsedTabularData {
  columns: string[];
  rows: Record<string, unknown>[];
}

export async function parseExcelFile(file: File): Promise<ParsedTabularData> {
  const data = new Uint8Array(await file.arrayBuffer());
  const workbook = XLSX.read(data, { type: 'array' });
  const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as unknown[][];

  if (jsonData.length < 2) {
    throw new Error('File must contain at least a header row and one data row.');
  }

  const columns = (jsonData[0] as string[]).map(c => String(c ?? ''));
  const rawRows = jsonData.slice(1) as unknown[][];
  const rows = rawRows
    .filter(row => row.some(cell => cell !== undefined && cell !== null && cell !== ''))
    .map(rowArr => {
      const obj: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        obj[col] = rowArr[i];
      });
      return obj;
    });

  return { columns, rows };
}

export async function parseCsvFile(file: File): Promise<ParsedTabularData> {
  const text = await file.text();
  const parsed = Papa.parse(text, { header: false });
  const jsonData = parsed.data as unknown[][];

  if (jsonData.length < 2) {
    throw new Error('File must contain at least a header row and one data row.');
  }

  const columns = (jsonData[0] as string[]).map(c => String(c ?? ''));
  const rawRows = jsonData.slice(1) as unknown[][];
  const rows = rawRows
    .filter(row => row.some(cell => cell !== undefined && cell !== null && cell !== ''))
    .map(rowArr => {
      const obj: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        obj[col] = rowArr[i];
      });
      return obj;
    });

  return { columns, rows };
}

export function parseKMLShapes(kmlText: string): (Point | Line | Polygon)[] {
  const parser = new DOMParser();
  const xml = parser.parseFromString(kmlText, 'text/xml');
  const placemarks = Array.from(xml.getElementsByTagName('Placemark'));
  const shapes: (Point | Line | Polygon)[] = [];

  placemarks.forEach(pm => {
    const name = pm.getElementsByTagName('name')[0]?.textContent || '';

    const point = pm.getElementsByTagName('Point')[0];
    if (point) {
      const coords = point.getElementsByTagName('coordinates')[0]?.textContent?.trim();
      if (coords) {
        const [lon, lat] = coords.split(',').map(Number);
        shapes.push({
          id: uuidv4(),
          type: 'point',
          name,
          description: '',
          colour: '#3388ff',
          icon: 'default',
          size: 24,
          isLocked: false,
          coordinates: [lat, lon],
        } as Point);
      }
    }

    const line = pm.getElementsByTagName('LineString')[0];
    if (line) {
      const coords = line.getElementsByTagName('coordinates')[0]?.textContent?.trim();
      if (coords) {
        const points = coords.split(/\s+/).map(pair => {
          const [lon, lat] = pair.split(',').map(Number);
          return [lat, lon] as [number, number];
        });
        shapes.push({
          id: uuidv4(),
          type: 'line',
          name,
          description: '',
          colour: '#3388ff',
          opacity: '1',
          borderColour: '#3388ff',
          borderWidth: 2,
          isLocked: false,
          coordinates: points,
          arrowStyle: 'arrow',
        } as Line);
      }
    }

    const polygon = pm.getElementsByTagName('Polygon')[0];
    if (polygon) {
      const coords = polygon.getElementsByTagName('coordinates')[0]?.textContent?.trim();
      if (coords) {
        const points = coords.split(/\s+/).map(pair => {
          const [lon, lat] = pair.split(',').map(Number);
          return [lat, lon] as [number, number];
        });
        shapes.push({
          id: uuidv4(),
          type: 'polygon',
          name,
          description: '',
          colour: '#3388ff',
          opacity: '0.5',
          borderColour: '#3388ff',
          borderWidth: 2,
          isLocked: false,
          coordinates: [points],
          createdAt: Date.now(),
        } as Polygon);
      }
    }
  });

  return shapes;
}

export interface GTFSParsedData {
  stops: Record<string, unknown>[];
  routes: Record<string, unknown>[];
  trips: Record<string, unknown>[];
  shapes: Record<string, unknown>[];
  stopTimes: Record<string, unknown>[];
}

export async function parseGTFSZip(
  file: File,
  onProgress?: (progress: number) => void
): Promise<{ points: Point[]; lines: Line[] }> {
  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);

  const requiredFiles = ['stops.txt', 'routes.txt', 'trips.txt', 'shapes.txt', 'stop_times.txt'];
  const missingFiles = requiredFiles.filter(f => !zipContent.files[f]);

  if (missingFiles.length > 0) {
    throw new Error(`Missing required GTFS files: ${missingFiles.join(', ')}`);
  }

  const data: GTFSParsedData = { stops: [], routes: [], trips: [], shapes: [], stopTimes: [] };
  let processed = 0;

  for (const fileName of requiredFiles) {
    const content = await zipContent.files[fileName].async('text');
    const parsed = Papa.parse(content, { header: true }).data as Record<string, unknown>[];

    switch (fileName) {
      case 'stops.txt': data.stops = parsed; break;
      case 'routes.txt': data.routes = parsed; break;
      case 'trips.txt': data.trips = parsed; break;
      case 'shapes.txt': data.shapes = parsed; break;
      case 'stop_times.txt': data.stopTimes = parsed; break;
    }
    processed++;
    onProgress?.(processed / requiredFiles.length * 100);
  }

  const points: Point[] = [];
  const lines: Line[] = [];

  data.stops.forEach((stop: any) => {
    const lat = parseFloat(stop.stop_lat);
    const lon = parseFloat(stop.stop_lon);
    if (!isNaN(lat) && !isNaN(lon)) {
      points.push({
        id: uuidv4(),
        type: 'point',
        name: stop.stop_name || 'Stop',
        description: stop.stop_desc || '',
        colour: '#FF0000',
        icon: 'location',
        size: 24,
        isLocked: false,
        coordinates: [lat, lon],
      });
    }
  });

  const shapesByRoute: Record<string, any[]> = {};
  data.shapes.forEach((shape: any) => {
    if (!shapesByRoute[shape.shape_id]) shapesByRoute[shape.shape_id] = [];
    shapesByRoute[shape.shape_id].push(shape);
  });

  Object.entries(shapesByRoute).forEach(([shapeId, shapePoints]) => {
    const sorted = shapePoints.sort((a, b) =>
      parseInt(a.shape_pt_sequence) - parseInt(b.shape_pt_sequence)
    );

    const trip = data.trips.find((t: any) => t.shape_id === shapeId);
    if (!trip) return;

    const route = data.routes.find((r: any) => r.route_id === (trip as any).route_id);
    if (!route) return;

    const coordinates: [number, number][] = sorted
      .map((s: any) => [parseFloat(s.shape_pt_lat), parseFloat(s.shape_pt_lon)])
      .filter((coord: number[]) => coord.length === 2 && !coord.some(isNaN)) as [number, number][];

    if (coordinates.length > 1) {
      const routeName = (route as any).route_long_name || (route as any).route_short_name || 'Route';
      const routeDescription = (route as any).route_long_name && (route as any).route_short_name
        ? `${(route as any).route_desc || ''} (${(route as any).route_short_name})`.trim()
        : (route as any).route_desc || '';

      lines.push({
        id: uuidv4(),
        type: 'line',
        name: routeName,
        description: routeDescription,
        colour: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
        opacity: '0.8',
        borderColour: '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0'),
        borderWidth: 2,
        isLocked: false,
        coordinates,
        arrowStyle: 'arrow',
      } as Line);
    }
  });

  return { points, lines };
}

export function parseExcelDateTime(value: unknown): string | null {
  if (typeof value === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const days = Math.floor(value);
    const msPerDay = 24 * 60 * 60 * 1000;
    const datePart = new Date(excelEpoch.getTime() + days * msPerDay);
    const fractionalDay = value - days;
    const totalSeconds = Math.round(fractionalDay * 86400);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const localDate = new Date(
      datePart.getFullYear(), datePart.getMonth(), datePart.getDate(),
      hours, minutes, seconds
    );
    return localDate.toISOString();
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    if (!isNaN(parsed)) return new Date(parsed).toISOString();
  }
  return null;
}

/**
 * Outer ring of a WKT POLYGON / MULTIPOLYGON (with or without Z/M) as [lat, lng] pairs,
 * or null if it isn't a usable polygon.
 * ponytail: takes the first ring only, so holes and extra MULTIPOLYGON parts are dropped.
 */
export function parseWktPolygonRing(wkt: string): [number, number][] | null {
  if (!/^\s*(MULTI)?POLYGON\b/i.test(wkt)) return null;
  const ring = wkt.match(/\(\s*([^()]+?)\s*\)/)?.[1];
  if (!ring) return null;
  const coords = ring.split(',').map(pair => {
    const [lng, lat] = pair.trim().split(/\s+/).map(Number);
    return [lat, lng] as [number, number];
  });
  const valid = coords.every(([lat, lng]) => Math.abs(lat) <= 90 && Math.abs(lng) <= 180);
  return valid && coords.length >= 3 ? coords : null;
}

/** "HH:MM" from an Excel time/datetime cell (a day fraction) or a string like "8:30 PM". */
export function toHHMM(value: unknown): string {
  if (typeof value === 'number') {
    const minutes = Math.round((value - Math.floor(value)) * 1440) % 1440;
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  }
  const match = String(value ?? '').match(/(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?\s*([ap])?\.?m?\b/i);
  if (!match) return '';
  let hours = Number(match[1]) % (match[3] ? 12 : 24);
  if (match[3]?.toLowerCase() === 'p') hours += 12;
  return `${String(hours).padStart(2, '0')}:${match[2]}`;
}
